import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, BookOpen, Sparkles, Check } from 'lucide-react';
import Header from './Header';

// Using relative URLs

const FRAMEWORKS = [
  { id: 'SOC 2', name: 'SOC 2', description: 'Service Organization Control 2' },
  { id: 'ISO 27001', name: 'ISO 27001', description: 'Information Security Management' },
  { id: 'GDPR', name: 'GDPR', description: 'General Data Protection Regulation' },
  { id: 'DPDP', name: 'DPDP', description: 'Digital Personal Data Protection' },
  { id: 'PCI DSS', name: 'PCI DSS', description: 'Payment Card Industry Data Security' }
];

const TrainingWizard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isGeneralMode = searchParams.get('mode') === 'general';
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedFrameworks, setSelectedFrameworks] = useState([]);
  const [trainingTopic, setTrainingTopic] = useState('');
  const [numQuestions, setNumQuestions] = useState(15);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [trainingData, setTrainingData] = useState({
    title: '',
    description: '',
    duration_minutes: 15
  });

  const handleFrameworkToggle = (frameworkId) => {
    if (selectedFrameworks.includes(frameworkId)) {
      setSelectedFrameworks(selectedFrameworks.filter(f => f !== frameworkId));
    } else {
      setSelectedFrameworks([...selectedFrameworks, frameworkId]);
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const handleGenerateTraining = async () => {
    // In general (framework-less) mode a topic is required to give the AI something to focus on.
    if (isGeneralMode) {
      if (!trainingTopic.trim()) {
        toast.error('Please enter a training topic');
        return;
      }
    } else if (selectedFrameworks.length === 0) {
      toast.error('Please select at least one framework');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `/api/trainings/generate`,
        {
          frameworks: isGeneralMode ? [] : selectedFrameworks,
          topic: trainingTopic || null,
          num_questions: Math.max(5, Math.min(50, parseInt(numQuestions, 10) || 15)),
        },
        getAuthHeaders()
      );
      
      if (response.data.error) {
        toast.error('Failed to generate training');
        console.error(response.data);
        return;
      }

      setGeneratedContent(response.data);
      setTrainingData({
        ...trainingData,
        title: response.data.title || 'New Training',
        description: response.data.summary ? response.data.summary.substring(0, 200) + '...' : ''
      });
      setStep(2);
      toast.success('Training content generated!');
    } catch (error) {
      toast.error('Failed to generate training');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTraining = async () => {
    setLoading(true);
    try {
      // Create training
      const createResponse = await axios.post(
        `/api/trainings`,
        {
          title: trainingData.title,
          description: trainingData.description,
          frameworks: isGeneralMode ? [] : selectedFrameworks,
          duration_minutes: trainingData.duration_minutes
        },
        getAuthHeaders()
      );

      const trainingId = createResponse.data.id;

      // Update with generated content
      await axios.put(
        `/api/trainings/${trainingId}`,
        {
          content: generatedContent.summary,
          questions: generatedContent.questions
        },
        getAuthHeaders()
      );

      toast.success('Training created successfully!');
      navigate('/trainings');
    } catch (error) {
      toast.error('Failed to create training');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-appbg">
      <Header />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => step === 1 ? navigate('/trainings') : setStep(step - 1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {step === 1 ? 'Back to Trainings' : 'Previous Step'}
        </Button>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${step >= 1 ? 'text-navy-600' : 'text-slate-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-navy-600 text-white' : 'bg-slate-200'}`}>
                {step > 1 ? <Check className="w-5 h-5" /> : '1'}
              </div>
              <span className="ml-2 font-medium">{isGeneralMode ? 'Topic' : 'Select Framework'}</span>
            </div>
            <div className="flex-1 h-1 mx-4 bg-slate-200">
              <div className={`h-full ${step >= 2 ? 'bg-navy-600' : 'bg-slate-200'}`} style={{width: step >= 2 ? '100%' : '0%'}}></div>
            </div>
            <div className={`flex items-center ${step >= 2 ? 'text-navy-600' : 'text-slate-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-navy-600 text-white' : 'bg-slate-200'}`}>
                2
              </div>
              <span className="ml-2 font-medium">Review & Create</span>
            </div>
          </div>
        </div>

        {/* Step 1: Select Framework (or general topic) */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <BookOpen className="w-6 h-6 mr-2 text-navy-600" />
                {isGeneralMode ? 'Create General Training' : 'Create Training Module'}
              </CardTitle>
              <CardDescription>
                {isGeneralMode
                  ? 'Enter a topic and AI will generate a training module with a required quiz — not linked to any compliance framework.'
                  : 'Select frameworks and AI will generate training content with quiz questions'}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {!isGeneralMode && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Select Compliance Framework(s)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {FRAMEWORKS.map((framework) => (
                      <div
                        key={framework.id}
                        onClick={() => handleFrameworkToggle(framework.id)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedFrameworks.includes(framework.id)
                            ? 'border-navy-600 bg-emerald-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-slate-900">{framework.name}</h3>
                            <p className="text-sm text-slate-600 mt-1">{framework.description}</p>
                          </div>
                          {selectedFrameworks.includes(framework.id) && (
                            <Check className="w-5 h-5 text-navy-600" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {isGeneralMode ? 'Training Topic' : 'Specific Topic (Optional)'}
                  {isGeneralMode && <span className="text-red-500 ml-1">*</span>}
                </label>
                <Input
                  type="text"
                  placeholder={isGeneralMode ? "e.g., Phishing Awareness · Secure Coding · Password Hygiene" : "e.g., 'Data Privacy' or 'Access Controls'"}
                  value={trainingTopic}
                  onChange={(e) => setTrainingTopic(e.target.value)}
                  className="max-w-md"
                  data-testid="training-topic-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {isGeneralMode ? 'AI will generate a training module and quiz on this topic.' : 'Leave blank for general framework training'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Number of Questions
                </label>
                <Input
                  type="number"
                  min={5}
                  max={50}
                  value={numQuestions}
                  onChange={(e) => setNumQuestions(e.target.value)}
                  className="max-w-[140px]"
                  data-testid="training-num-questions"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Allowed range: 5–50. Default 15.
                </p>
              </div>

              <div className="pt-4">
                <Button
                  onClick={handleGenerateTraining}
                  disabled={loading || (isGeneralMode ? !trainingTopic.trim() : selectedFrameworks.length === 0)}
                  className="w-full bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-700 hover:to-navy-800 text-white"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {loading ? 'Generating Training Content...' : 'Generate Training with AI'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Review & Create */}
        {step === 2 && generatedContent && (
          <div className="space-y-6">
            {/* Training Details */}
            <Card>
              <CardHeader>
                <CardTitle>Training Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
                  <Input
                    value={trainingData.title}
                    onChange={(e) => setTrainingData({...trainingData, title: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                  <Textarea
                    value={trainingData.description}
                    onChange={(e) => setTrainingData({...trainingData, description: e.target.value})}
                    rows={3}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Duration (minutes)</label>
                  <Input
                    type="number"
                    value={trainingData.duration_minutes}
                    onChange={(e) => setTrainingData({...trainingData, duration_minutes: parseInt(e.target.value)})}
                    className="max-w-xs"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Frameworks</label>
                  <div className="flex flex-wrap gap-2">
                    {selectedFrameworks.map((fw) => (
                      <Badge key={fw} variant="outline">
                        {fw}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Training Content Preview */}
            <Card>
              <CardHeader>
                <CardTitle>Training Content</CardTitle>
                <CardDescription>AI-generated training material</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <p className="text-slate-700 whitespace-pre-wrap">{generatedContent.summary}</p>
                </div>
              </CardContent>
            </Card>

            {/* Quiz Questions Preview */}
            <Card>
              <CardHeader>
                <CardTitle>Quiz Questions ({generatedContent.questions?.length || 0})</CardTitle>
                <CardDescription>Employees must answer these to complete the training</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {generatedContent.questions?.map((q, index) => (
                  <div key={q.id} className="p-4 border rounded-lg bg-slate-50">
                    <p className="font-semibold text-slate-900 mb-3">
                      {index + 1}. {q.question}
                    </p>
                    <div className="space-y-2 ml-4">
                      {Object.entries(q.options).map(([key, value]) => (
                        <div key={key} className="flex items-start">
                          <span className={`font-medium mr-2 ${key === q.correct_answer ? 'text-navy-600' : 'text-slate-600'}`}>
                            {key}.
                          </span>
                          <span className={key === q.correct_answer ? 'text-navy-600 font-medium' : 'text-slate-700'}>
                            {value} {key === q.correct_answer && '✓'}
                          </span>
                        </div>
                      ))}
                    </div>
                    {q.explanation && (
                      <p className="text-sm text-slate-600 mt-3 ml-4 italic">
                        Explanation: {q.explanation}
                      </p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleCreateTraining}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-navy-600 to-navy-700 hover:from-navy-700 hover:to-navy-800 text-white"
              >
                {loading ? 'Creating...' : 'Create Training'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingWizard;
